import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      // Server sets httpOnly cookies; we do NOT store the token in localStorage.
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Non-fatal: cookies may already be cleared; log and continue signing out locally.
      console.warn("Logout request failed; clearing local state anyway:", err?.message || err);
    }
    setUser(false);
  }, []);

  // Stable context value — prevents unnecessary re-renders of every consumer on parent re-render.
  const value = useMemo(
    () => ({ user, login, logout, error, refresh: load }),
    [user, login, logout, error, load]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
